import { useCan } from '@/components/can/Can';
import { DataTable } from '@/components/dataTable';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';
import { useConfirmationStore } from '@/components/dialog/confirmation.store';
import { useDialogFormStore } from '@/components/dialog/form.store';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { Translation, useDynamicLocale } from '@/hoslog-intl';
import { additionalValue, Pagination } from '@/react-query/context/ApiClientContextController';
import { PlanManagement } from '@/services/planManagement/planManagement.types';
import { useParams } from '@tanstack/react-router';
import * as React from 'react';
import ClubPlanForm from '../../form/ClubPlanForm';
import { useGetTimeInterval } from '../../hooks/useGetTimeInterval';
import { useUpdateClubPlanStatus } from '../../hooks/useManageOriginalPlansMutation';
import { getColumns } from './Column';

export function PlanManagementTable({
  data,
  isLoading,
  pagination,
  commonClubId,
}: {
  data: PlanManagement[];
  isLoading: boolean;
  pagination: Pagination;
  commonClubId: additionalValue;
}) {
  const pageCount = React.useMemo(
    () => Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize ?? 1)),
    [pagination.totalItems, pagination.pageSize],
  );

  const { t } = useDynamicLocale();
  const { onOpen } = useDialogFormStore();
  const { openConfirmation } = useConfirmationStore();
  const { mutateAsync } = useUpdateClubPlanStatus();

  const { clubid } = useParams({ from: '/(app)/store-information/premium-stores/plan-management/$clubid/' });

  const columns = React.useMemo(
    () => getColumns(openConfirmation, onOpen, mutateAsync, t, clubid, commonClubId),
    [onOpen, t, openConfirmation, mutateAsync, clubid, commonClubId],
  );

  const { data: lastEntryOptions = [] } = useGetTimeInterval(clubid);

  const isActionKeyDisabled = React.useMemo(() => {
    const clubPlanCount = data.filter((plan) => plan.planOrigin === 'Club Plan').length;
    return clubPlanCount >= 3 || lastEntryOptions.length === 0;
  }, [data, lastEntryOptions.length]);

  const canAddNew = useCan({ allOf: [PERMS.club.premium.plan.add] });

  const { table } = useDataTable({
    data,
    columns,
    pageCount: pageCount,
    initialState: {
      sorting: [{ id: 'id', desc: true }],
    },
    meta: {
      title: <Translation id="storeManagement.premiumStores.planManagement.table.title" />,
      loading: isLoading,
      totalPage: pagination.totalItems,
      actionKey: canAddNew ? 'onAddNew' : '',
      actionDisabled: isActionKeyDisabled,
      action: () => {
        onOpen({
          modalTitle: 'common.page.modal.manageOriginalPlans',
          component: <ClubPlanForm clubId={clubid} commonClubId={commonClubId?.commonClubId} />,
          okText: t('common.page.button.submit'),
          size: 'lg',
          title: 'Manage Original Plans',
          cancelText: t('common.page.button.cancel'),
          onCancel: () => {},
        });
      },
    },
    getRowId: (originalRow) => String(originalRow.id),
    shallow: false,
    clearOnDefault: true,
  });

  return (
    <>
      <DataTable table={table}>
        <DataTableToolbar />
      </DataTable>
    </>
  );
}
